import { Injectable } from 'angular-ts-decorators';

import { SpynlBase } from '@shared/app/scripts/services/spynl-base';
import type { IConfig } from '@shared/app/scripts/services/config';
import { Domain } from '@shared/app/scripts/services/domain';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';
import { EosModel } from '@shared/app/scripts/classes/eos-model';

/**
 * Factory in the sharedModule. It takes care of communication with eos
 * resource. Methods are inherited form {@link shared.spynlBase
 * spynlBase} service, and are documented there.
 *
 * # Data schema
 * {@link http://docs.dev.swcloud.nl/schemas/bower_components/docson/#../../spynl/eos.json
 * is documented with json-schema}
 */

@Injectable('eos')
export class Eos extends SpynlBase<EosModel> {
  protected location: string;

  constructor(
    private moment,
    protected $http: ng.IHttpService,
    protected $q: ng.IQService,
    protected sentry,
    protected domain: Domain,
    protected config: IConfig,
    protected standardErrorParser: IStandardErrorParser,
  ) {
    'ngInject';

    super($http, $q, sentry, domain, config, standardErrorParser, EosModel);
    this.location = '/eos';
  }

  /**
   *
   * Queries all eos that starts or finish on the day
   * @param {Date} date
   * Day to get all the eos for. Time part is going to be ignored. We are
   * using the same time zone that is defined on the browser, which is smart
   * enough to know about summer/winter time differences.
   * @returns {promise} it will resolve with data, or fail with error
   * description
   */
  queryPerDay(date: Date) {
    const dateMoment = this.moment(date);

    return this.filter({
      startDate: dateMoment.startOf('day').format(),
      endDate: dateMoment.endOf('day').format(),
    });
  }

  /**
   * Loads eos exported as pdf.
   * @param {string} _id eos _id
   * @return {ng.IPromise<string>}
   * Will return the order pdf as string
   */
  download(_id: string): ng.IPromise<string> {
    const spynlUrl = `//${this.domain.sibling(
      this.config.spynlServices,
    )}/eos/download`;

    return this.$http
      .post(
        spynlUrl,
        { _id },
        {
          responseType: 'arraybuffer',
          withCredentials: true,
        },
      )
      .then((response) => <string>response.data);
  }

  /**
   * Reset eos
   * @param {string} _id eos _id
   * @return {ng.IPromise<string>}
   * Http promise
   */
  reset(_id) {
    return this.$http.post(this.getLocation() + '/reset', {
      filter: { _id },
    });
  }

  /**
   * create a rectification document
   * @param {string} userId the device user._id
   * @param {number} value a float value which will be used as the start/end balance
   * @param {string} remarks remarks to give a reason for creating the document
   * @param {string} tenantId optional tenantId for admin users
   * @return {ng.IPromise<string>}
   * Http promise
   */
  rectify(
    userId: string,
    value: number,
    remarks: string,
    tenantId?: string,
  ): ng.IPromise<any> {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http.post(
      `//${this.domain.sibling(
        this.config.spynlSubdomain,
      )}${tenantSection}/eos/rectify`,
      {
        userId,
        value,
        remarks,
      },
    );
  }

  /**
   * @param {string} _id
   * eos id
   * @param {string[]} recipients
   * emails we are sending to
   * @param {string} message
   * Optional. Message to be added to the email
   * Send eos to an email address, by exposing {@link
   * https://softwearconnect.gitlab.io/spynl.app/#/services/post_eos_email
   * spynl/eos/email} to frontend.
   * @returns {promise}
   * resolve with response on success, otherwise reject with the error
   */
  email(_id: string, recipients: string[], message?: string): ng.IPromise<{}> {
    return this.$http
      .post(
        this.domain.getSpynl() + '/eos/email',
        {
          _id,
          recipients,
          message,
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((response: any) => response.data.data);
  }

  /**
   * Start the EOS process int he POS by exposing {@link
   * https://softwearconnect.gitlab.io/spynl.app/#/data/post_eos_init
   * spynl/eos/init} to frontend.
   * @returns {promise}
   * resolve with response on success, otherwise reject with the error
   */
  init(): ng.IPromise<{}> {
    return this.$http
      .post(
        this.domain.getSpynl() + '/eos/init',
        {},
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((response: any) => response.data.data);
  }

  /**
   * Start the EOS process int he POS by exposing {@link
   * https://softwearconnect.gitlab.io/spynl.app/#/data/post_eos_get_overview
   * spynl/eos/get-overview} to frontend.
   * @returns {promise}
   * resolve with response on success, otherwise reject with the error
   */
  getOverview(deviceId: string): ng.IPromise<{
    // TODO provide complete type
    turnover: number;
  }> {
    return this.$http
      .post(
        this.domain.getSpynl() + '/eos/get-overview',
        {
          filter: { deviceId },
        },
        {
          // done on config level for v7; setting for v6
          withCredentials: true,
        },
      )
      .then((response: any) => response.data.data);
  }
}
